import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { threadCopy } from '@/copy'
import { Alert } from '../Alert/Alert'
import { Avatar } from '../Avatar/Avatar'
import { Button } from '../Button/Button'
import { cx } from '../cx'
import { Disclosure } from '../Disclosure/Disclosure'
import { ToolCall, type ToolCallProps } from '../ToolCall/ToolCall'
import { Markdown } from './Markdown'
import styles from './Message.module.css'

export type MessageState = 'settled' | 'streaming' | 'failed'

const timeFormat = new Intl.DateTimeFormat(undefined, {
  hour: '2-digit',
  minute: '2-digit',
})

const compact = new Intl.NumberFormat(undefined, {
  notation: 'compact',
  maximumFractionDigits: 1,
})

export function Message({
  author,
  timestamp,
  body,
  reasoning,
  toolCalls,
  resources,
  state = 'settled',
  error,
  onRetry,
  onMentionClick,
  className,
}: {
  author: { id: string; name: string; kind: 'human' | 'agent'; href?: string }
  timestamp?: number
  body: string
  reasoning?: string
  toolCalls?: readonly (ToolCallProps & { id: string })[]
  resources?: ReactNode
  state?: MessageState
  error?: string
  onRetry?: () => void
  onMentionClick?: (name: string) => void
  className?: string
}) {
  return (
    <article
      className={cx(styles.message, className)}
      data-state={state}
      aria-busy={state === 'streaming' || undefined}
    >
      <Avatar name={author.name} kind={author.kind} size="md" />
      <div className={styles.column}>
        <div className={styles.text}>
          <div className={styles.authorLine}>
            {author.href ? (
              <Link to={author.href} className={styles.authorLink}>
                {author.name}
              </Link>
            ) : (
              <span className={styles.author}>{author.name}</span>
            )}
            {timestamp != null && (
              <time
                className={styles.time}
                dateTime={new Date(timestamp).toISOString()}
              >
                {timeFormat.format(timestamp)}
              </time>
            )}
          </div>
          {reasoning?.trim() && (
            <Disclosure
              label={threadCopy.message.reasoning}
              count={compact.format(reasoning.length)}
            >
              <p className={styles.reasoning}>{reasoning}</p>
            </Disclosure>
          )}
          <Markdown
            className={styles.body}
            source={body}
            streaming={state === 'streaming'}
            onMentionClick={onMentionClick}
          />
        </div>
        {state === 'failed' && error && (
          <Alert tone="danger">
            <span className={styles.alertRow}>
              {error}
              {onRetry && (
                <Button variant="outline" size="sm" onClick={onRetry}>
                  {threadCopy.message.retry}
                </Button>
              )}
            </span>
          </Alert>
        )}
        {author.kind === 'agent' && toolCalls?.length ? (
          <div className={styles.toolCalls}>
            {toolCalls.map(({ id, ...call }) => (
              <ToolCall key={id} {...call} />
            ))}
          </div>
        ) : null}
        {resources}
      </div>
    </article>
  )
}
